/**
 * Team Members Page Component
 * Task 5.6: Create Team Members Page
 */

import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Users, Plus, AlertCircle } from 'lucide-react';
import { PageContainer } from '@/components/layout/PageContainer';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { TeamMembersList } from '@/components/TeamMembers/TeamMembersList';
import { AddTeamMemberForm } from '@/components/TeamMembers/AddTeamMemberForm';
import { EditTeamMemberModal } from '@/components/TeamMembers/EditTeamMemberModal';
import { teamMemberService } from '@/lib/api/teamMember.service';
import { useTeamMemberRole } from '@/hooks/useTeamMemberRole';
import { canManageTeam } from '@/utils/permissions';
import type { TeamMemberWithUser } from '@/types/teamMember.types';

export const TeamMembersPage: React.FC = () => {
  const { celebrationId } = useParams<{ celebrationId: string }>();
  const { t } = useTranslation(['celebrations', 'common']);

  const celebrationIdNum = celebrationId ? parseInt(celebrationId) : 0;
  const { role } = useTeamMemberRole(celebrationIdNum);
  
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMemberWithUser | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const canEdit = canManageTeam(role);

  const handleAddSuccess = () => {
    setShowAddForm(false);
    setRefreshTrigger(prev => prev + 1);
  };

  const handleEditSuccess = () => {
    setEditingMember(null);
    setRefreshTrigger(prev => prev + 1);
  };

  const handleRemove = async (member: TeamMemberWithUser) => {
    if (!window.confirm(t('celebrations:team.removeConfirm', { name: member.user.full_name }))) {
      return;
    }

    try {
      setError(null);
      await teamMemberService.removeTeamMember(celebrationIdNum, member.id);
      setRefreshTrigger(prev => prev + 1);
    } catch (err: any) {
      console.error('Error removing team member:', err);
      setError(err.response?.data?.error || t('celebrations:team.removeError'));
    }
  };

  if (!celebrationId) {
    return (
      <PageContainer width="wide">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              <span>{t('celebrations:team.invalidId')}</span>
            </div>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer width="wide" className="space-y-4">
      <AppBreadcrumbs />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="w-6 h-6" />
            {t('celebrations:team.title')}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t('celebrations:team.subtitle')}
          </p>
        </div>
        {canEdit && !showAddForm && (
          <Button onClick={() => setShowAddForm(true)}>
            <Plus className="w-4 h-4 mr-2" />
            {t('celebrations:team.addMember')}
          </Button>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md text-red-600">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Add Form */}
      {showAddForm && (
        <div className="mb-6">
          <AddTeamMemberForm
            celebrationId={celebrationIdNum}
            onSuccess={handleAddSuccess}
            onCancel={() => setShowAddForm(false)}
          />
        </div>
      )}

      {/* Team Members List */}
      <div>
        <Card>
          <CardHeader>
            <CardTitle>{t('celebrations:team.currentTeam')}</CardTitle>
            <CardDescription>
              {t('celebrations:team.currentTeamDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <TeamMembersList
              celebrationId={celebrationIdNum}
              canEdit={canEdit}
              onEdit={setEditingMember}
              onRemove={handleRemove}
              refreshTrigger={refreshTrigger}
            />
          </CardContent>
        </Card>
      </div>

      {/* Edit Modal */}
      {editingMember && (
        <EditTeamMemberModal
          member={editingMember}
          celebrationId={celebrationIdNum}
          onSuccess={handleEditSuccess}
          onCancel={() => setEditingMember(null)}
        />
      )}
    </PageContainer>
  );
};
